import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

interface TypingAnimationOptions {
  autoStart?: boolean
  typingSpeed?: number
}

export default function useTypingAnimation(text: string, options: TypingAnimationOptions = {}) {
  const { autoStart, typingSpeed = 20 } = options

  const [animatedText, setAnimatedText] = useState('')
  const [isTyping, setIsTyping] = useState(!!autoStart)

  const frameIdRef = useRef<NodeJS.Timeout | undefined>(undefined)
  const currentIndexRef = useRef(0)

  const animateTyping = useCallback(() => {
    let currentIndex = 0

    setIsTyping(true)

    frameIdRef.current = setInterval(() => {
      const incrementedText = text.slice(0, currentIndex + 1)

      setAnimatedText(incrementedText)

      currentIndex++

      if (currentIndex > text.length) {
        setIsTyping(false)
      }
    }, typingSpeed)
  }, [text, typingSpeed])

  const startAnimation = useCallback(() => {
    if (isTyping) return
    animateTyping()
  }, [animateTyping, isTyping])

  const stopAnimation = useCallback(() => {
    clearInterval(frameIdRef.current)
    setIsTyping(false)
  }, [])

  const resetAnimation = useCallback(() => {
    clearInterval(frameIdRef.current)
    currentIndexRef.current = 0
    frameIdRef.current = null
    setAnimatedText('')
    setIsTyping(false)
  }, [])

  useEffect(() => {
    if (autoStart) {
      animateTyping()
    }

    return () => {
      resetAnimation()
    }
  }, [animateTyping, resetAnimation, autoStart])

  const memoizedResponse = useMemo(() => {
    return { animatedText, isTyping, startAnimation, stopAnimation, resetAnimation }
  }, [animatedText, isTyping, startAnimation, stopAnimation, resetAnimation])

  return memoizedResponse
}
